import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when strategem 113 (Demoralizing Warcry) is played and trigger shockwave effect
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID
 * @returns {Object} - { activeShockwaves, triggerShockwave }
 */
export const useStrategemShockwave = (gameState, userId) => {
  const [activeShockwaves, setActiveShockwaves] = useState([]);
  const processedStrategemsRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  // Get center position where all 4 zones meet (opponent_front, opponent_flank, your_front, your_flank)
  const getZoneIntersectionCenter = () => {
    // Find all 4 zone elements
    const opponentFront = document.querySelector('.battle-zone.opponent-front');
    const opponentFlank = document.querySelector('.battle-zone.opponent-flank');
    const yourFront = document.querySelector('.battle-zone.front');
    const yourFlank = document.querySelector('.battle-zone.flank');
    
    // If we have all 4 zones, calculate the intersection point
    if (opponentFront && opponentFlank && yourFront && yourFlank) {
      const opponentFrontRect = opponentFront.getBoundingClientRect();
      const opponentFlankRect = opponentFlank.getBoundingClientRect();
      const yourFrontRect = yourFront.getBoundingClientRect();
      const yourFlankRect = yourFlank.getBoundingClientRect();
      
      // The center is where the zones meet:
      // - Horizontally: between opponent_front right edge and opponent_flank left edge (or vice versa)
      //   Actually, it's the midpoint between the two battlefield-containers
      // - Vertically: between opponent zones bottom and your zones top
      
      // Get the two battlefield-containers
      const battlefieldContainers = document.querySelectorAll('.battlefield-container');
      if (battlefieldContainers.length >= 2) {
        const opponentContainer = battlefieldContainers[0]; // Top container (opponent zones)
        const yourContainer = battlefieldContainers[1]; // Bottom container (your zones)
        
        const opponentRect = opponentContainer.getBoundingClientRect();
        const yourRect = yourContainer.getBoundingClientRect();
        
        // Horizontal center: middle of the containers (they should be aligned)
        const centerX = opponentRect.left + opponentRect.width / 2;
        
        // Vertical center: midpoint between opponent container bottom and your container top
        const centerY = (opponentRect.bottom + yourRect.top) / 2;
        
        return { x: centerX, y: centerY };
      }
      
      // Fallback: calculate from zone positions
      // Horizontal: midpoint between front and flank zones
      const horizontalCenter = (opponentFrontRect.right + opponentFlankRect.left) / 2;
      // Vertical: midpoint between opponent zones bottom and your zones top
      const verticalCenter = (opponentFrontRect.bottom + yourFrontRect.top) / 2;
      
      return { x: horizontalCenter, y: verticalCenter };
    }
    
    // Fallback: try to find any battlefield container
    const battlefieldElement = document.querySelector('.battlefield-container');
    if (battlefieldElement) {
      const rect = battlefieldElement.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
    
    // Final fallback: screen center
    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    };
  };

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    // Skip first render to avoid triggering on initial load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const allPlayerIds = Object.keys(gameState.state || {});

    // Check all players' completed orders for strategem 113
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach(strategem => {
        if (!strategem || strategem.id !== 113) return;

        // Create unique key for this strategem play
        const strategemKey = `strategem-113-${strategem.instanceId}-${playerId}`;

        // Skip if already processed
        if (processedStrategemsRef.current.has(strategemKey)) {
          return;
        }

        processedStrategemsRef.current.add(strategemKey);

        console.log('🌊 useStrategemShockwave: Detected strategem 113 (Demoralizing Warcry)', {
          strategemInstanceId: strategem.instanceId,
          playerId,
          isCurrentPlayer: String(playerId) === String(userId)
        });

        // Determine shockwave origin position
        // Strategem 113 affects all enemy units, so the shockwave should originate from
        // the center point where all 4 zones meet (opponent_front, opponent_flank, your_front, your_flank)
        const shockwavePosition = getZoneIntersectionCenter();

        // Trigger shockwave effect
        const animationKey = `shockwave-113-${strategem.instanceId}-${Date.now()}`;
        setActiveShockwaves(prev => [
          ...prev,
          {
            key: animationKey,
            x: shockwavePosition.x,
            y: shockwavePosition.y,
            strategemInstanceId: strategem.instanceId,
            playerId
          }
        ]);

        // Clean up after animation completes (handled by component)
        setTimeout(() => {
          setActiveShockwaves(prev => prev.filter(sw => sw.key !== animationKey));
        }, 1000); // Slightly longer than animation duration
      });
    });

    // Clean up old processed strategems (keep last 50)
    if (processedStrategemsRef.current.size > 50) {
      const keys = Array.from(processedStrategemsRef.current);
      keys.slice(0, keys.length - 50).forEach(key => {
        processedStrategemsRef.current.delete(key);
      });
    }
  }, [gameState, userId]);

  const completeShockwave = (key) => {
    setActiveShockwaves(prev => prev.filter(sw => sw.key !== key));
  };

  return {
    activeShockwaves,
    completeShockwave
  };
};

export default useStrategemShockwave;
